import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { dueAtInDays, dueAtOn } from "../../lib/lane-due";
import { queryKeys } from "../../lib/queryKeys";
import type { CrmContact, PendingAttachment } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { cn } from "../ui/cn";
import { Field, Input, Picker } from "./fields";
import { formatBytes } from "./attachment-format";
import { toHtml } from "./note-html";

/** Exported so the company header can open the composer on a chosen mode. */
export type ComposerMode =
  | "note"
  | "call"
  | "whatsapp"
  | "email"
  | "meeting"
  | "external_ticket"
  | "quote"
  | "order"
  | "task";
type Mode = ComposerMode;

/** The mode row, in the order somebody reaching for one would scan. */
const MODES: { id: Mode; label: string }[] = [
  { id: "note", label: "Note" },
  { id: "call", label: "Call" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "email", label: "Email" },
  { id: "meeting", label: "Meeting" },
  { id: "external_ticket", label: "Ticket" },
  { id: "quote", label: "Quote" },
  { id: "order", label: "Order" },
  { id: "task", label: "Task" },
];

/** The two modes that are one endpoint with a type on it. */
const MESSAGE_MODES = new Set<Mode>(["whatsapp", "email"]);

/**
 * "Who started it", in the words each channel uses.
 *
 * Worth the three lines rather than a generic inbound/outbound pair: "They
 * rang us" is instantly readable where "Inbound" needs a beat of thought, and
 * a rep logging a call between customers is doing it at speed.
 */
const DIRECTIONS: Record<"call" | "whatsapp" | "email", { value: string; label: string }[]> = {
  call: [
    { value: "inbound", label: "They rang us" },
    { value: "outbound", label: "We rang them" },
  ],
  whatsapp: [
    { value: "inbound", label: "They messaged us" },
    { value: "outbound", label: "We messaged them" },
  ],
  email: [
    { value: "inbound", label: "They emailed us" },
    { value: "outbound", label: "We emailed them" },
  ],
};

/**
 * Now, in the form a `datetime-local` input takes: "2026-08-13T14:05".
 *
 * Built by hand rather than sliced off `toISOString()`, which is UTC -- for
 * half the year that would offer a British desk a meeting an hour ago.
 */
function localNow(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, "0");

  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  );
}

/**
 * A meeting's time, in the wording the meetings diary uses for the same
 * sentence -- "Thu 17 Sep, 14:30".
 *
 * `en-GB` explicitly rather than the browser's locale, because this string is
 * written into a Lane task the whole desk reads: an agent whose laptop is set
 * to US English should not be the reason one task on the board says
 * "Sep 17, 2:30 PM" and the next says "17 Sep, 14:30".
 */
function meetingWhen(localValue: string): string {
  const at = new Date(localValue);
  /* Unreachable from the form, which only submits a parsed date -- but this
     sentence ends up in front of somebody, and "Invalid Date" in a task
     description is worse than a phrase that admits it does not know. */
  if (Number.isNaN(at.getTime())) return "the date recorded";

  return at.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A typed-in money box, to a number of pounds -- or `undefined` for "not said".
 *
 * Blank, a stray "£" and a word all come back undefined rather than 0, because
 * zero is a real figure: an order genuinely worth nothing is different from an
 * order whose value nobody typed, and the panel says so differently.
 */
function parseMoney(raw: string): number | undefined {
  const cleaned = raw.replace(/[£,\s]/g, "");
  if (cleaned === "") return undefined;

  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return undefined;

  /* To the penny. Float pounds are exact enough at these sizes, and rounding
     here keeps 1/3 of a split total out of the stored figure. */
  return Math.round(value * 100) / 100;
}

/** The value box, worded the same on a quote and on an order. */
function MoneyField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label="Value" hint="Net of VAT, in pounds — optional">
      <Input value={value} onChange={onChange} placeholder="0.00" />
    </Field>
  );
}

/**
 * Record something that happened, or raise something that should.
 *
 * One control with five modes rather than five buttons: a rep coming off a call
 * has one thing to write down and should not first have to decide which of five
 * scattered affordances they want. The mode row is the decision, and it is one
 * click away from the box they were going to type in anyway.
 *
 * Notes are the default because they are what gets written most, and because
 * every other mode is a note with extra structure.
 */
export function ActivityComposer({
  companyId,
  companyName,
  contacts,
  taskBlockedReason,
  onDone,
  mode,
  onModeChange,
}: {
  companyId: number;
  /**
   * Only used to word a follow-up's description, which is read in Lane where
   * nothing else says which account a task belongs to.
   */
  companyName: string;
  contacts: CrmContact[];
  /**
   * Why tasks are unavailable, or null when they are not. A reason rather than
   * a flag: "the server has no Lane" and "you have not connected yours" are
   * somebody else's problem and yours, and they need different advice.
   */
  taskBlockedReason: string | null;
  onDone: () => void;
  /**
   * Which kind of thing is being recorded, or null when the composer is shut.
   *
   * Controlled by the page rather than held here, so "Add task" in the company
   * header and in the Tasks filter open the same one control on the right mode
   * -- rather than each growing its own dialog that drifts from this.
   */
  mode: Mode | null;
  onModeChange: (mode: Mode | null) => void;
}) {
  const open = mode !== null;
  /*
   * Set by the mode row inside this form, so the effect below can tell a mode
   * somebody picked *while looking at the composer* from one chosen somewhere
   * else on the page. Only the second wants the page moved.
   */
  const pickedHere = useRef(false);
  const setMode = (next: Mode): void => {
    pickedHere.current = true;
    onModeChange(next);
  };
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [contactId, setContactId] = useState("");
  const [direction, setDirection] = useState<"inbound" | "outbound">("outbound");
  const [duration, setDuration] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [location, setLocation] = useState("");
  const [reference, setReference] = useState("");
  /*
   * Open, not lost.
   *
   * It defaulted to lost when the only way here was the mode row, where a quote
   * was written up afterwards to say why it went -- "why it was won or lost" is
   * still what the box below asks for. "Add quote" in the panel is the other
   * occasion: a quote that has just gone out and is waiting on an answer. That
   * is now the common one, and the safe default is the state that asks somebody
   * to chase rather than the one that writes the account off unread.
   */
  const [outcome, setOutcome] = useState<"won" | "lost" | "open">("open");
  /*
   * Held as the typed string rather than a number, like every other field
   * here. A number state would have to decide what "" and "12." mean while
   * somebody is still typing, and would fight the caret to do it.
   */
  const [netTotal, setNetTotal] = useState("");
  /** Empty until somebody overrides the account manager. */
  const [assigneeId, setAssigneeId] = useState("");
  const [changingAssignee, setChangingAssignee] = useState(false);
  /*
   * The follow-ups queued against a meeting being written up -- which quick
   * tasks are ticked, one typed one, and who they land on. See the section
   * under the body.
   */
  const [quickPicked, setQuickPicked] = useState<number[]>([]);
  const [followUpTitle, setFollowUpTitle] = useState("");
  const [followUpDue, setFollowUpDue] = useState("");
  /** Null means "not touched", which is what lets the default arrive with Lane. */
  const [followUpBy, setFollowUpBy] = useState<string | null>(null);
  const [followUpError, setFollowUpError] = useState<string | null>(null);
  /*
   * The meeting this form has already written down, set only when a follow-up
   * failed afterwards and left the composer open. It is what stops a retry
   * logging the same visit twice.
   */
  const [loggedMeeting, setLoggedMeeting] = useState<number | null>(null);
  /** Uploaded already, tied to nothing until this form is saved. */
  const [files, setFiles] = useState<PendingAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const bodyBox = useRef<HTMLTextAreaElement>(null);

  const taskMode = mode === "task" && taskBlockedReason === null;
  /*
   * A meeting can raise the same tasks, so it needs the same Lane data --
   * the boards to know where they would go, and the member list to name who
   * takes them.
   */
  const followUpMode = mode === "meeting" && taskBlockedReason === null;

  /*
   * Bring the composer to whoever opened it.
   *
   * On a phone this is the difference between the button working and the
   * button appearing to do nothing. The company page is one column below `xl`,
   * and the timeline sits under the details, the contacts and the group panel
   * -- so "Add note" in the header opened this form about two screens further
   * down than the thumb that pressed it, with no scroll and nothing focused.
   * The composer was open the whole time. Nobody could tell.
   *
   * Not on every mode change: the row inside this form is somebody already
   * looking at it, and yanking the page under them to show them what they can
   * already see is its own bug. `pickedHere` is that distinction. The timeline's
   * empty-state buttons are external and do want it -- they are as far from
   * this form as the header is.
   *
   * `preventScroll` on the focus because the line above it has already decided
   * where the form should sit, and letting the browser scroll to the textarea
   * as well lands it somewhere neither of them chose.
   *
   * **Not `behavior: "smooth"`**, which was the first attempt and was silently
   * dropped: measured on the company page, a smooth scroll of this form left
   * the timeline's scroller on 0 while the same call with `auto` moved it 904
   * pixels. A scroll that sometimes does not happen is the bug this is here to
   * fix, and landing on the composer at once is no worse than gliding to it --
   * the keyboard is coming up over it either way.
   */
  useEffect(() => {
    const internal = pickedHere.current;
    pickedHere.current = false;
    if (internal || mode === null) return;

    form.current?.scrollIntoView({ block: "start", behavior: "auto" });
    bodyBox.current?.focus({ preventScroll: true });
  }, [mode]);

  /*
   * A meeting opens on now, rather than on an empty box.
   *
   * Almost every meeting logged here is one that just finished, so the empty
   * field was a required question whose answer was already known -- and
   * "When" is the one thing that stops a meeting being saved. Filled rather
   * than merely suggested, so it is a value somebody corrects instead of a
   * placeholder they have to supply; the input is a picker either way.
   *
   * Only when the field is untouched, so switching Meeting → Call → Meeting
   * does not overwrite a time already chosen.
   */
  useEffect(() => {
    if (mode !== "meeting") return;
    setStartsAt((current) => current || localNow());
  }, [mode]);

  /*
   * A meeting logged but left open by a follow-up that would not go, then
   * abandoned for a different kind of entry. Cleared, so the next thing
   * somebody writes cannot inherit its id and quietly hang a task off it.
   */
  useEffect(() => {
    if (mode !== "meeting") setLoggedMeeting(null);
  }, [mode]);

  /*
   * The sales boards. Read not to populate a dropdown -- there is no longer one
   * -- but to name the board this agent's tasks will land on, and to say so
   * plainly when there is not one.
   */
  const boards = useQuery({
    queryKey: queryKeys.crmLaneBoards(companyId),
    queryFn: () => crmApi.laneBoards(companyId),
    enabled: taskMode || followUpMode,
    staleTime: 5 * 60_000,
  });

  /*
   * The desk's standing jobs, the same list the meetings diary and the Tasks
   * section offer. Only fetched where they are shown.
   */
  const quickTasks = useQuery({
    queryKey: queryKeys.crmQuickTasks,
    queryFn: crmApi.quickTasks,
    enabled: followUpMode,
    staleTime: 5 * 60_000,
  });

  /*
   * Who this account has actually dealt with most. Offered as a suggestion
   * rather than applied: the account manager is a decision somebody made, and
   * a ranking is evidence -- so it only becomes the default when nobody
   * manages the account, and otherwise sits beside the picker as a shortcut.
   */
  const contacted = useQuery({
    queryKey: queryKeys.crmOwnerSuggestion(companyId),
    queryFn: () => crmApi.ownerSuggestion(companyId),
    enabled: taskMode,
    staleTime: 5 * 60_000,
  });

  const suggested = boards.data?.suggestedBoardId ?? null;
  const destination =
    boards.data?.boards.find((board) => board.id === suggested)?.name ?? null;
  const noBoard = boards.data !== undefined && boards.data.boards.length === 0;

  /*
   * A configured board name that matched nothing in Lane, while others did.
   *
   * Worth saying even though a board *was* found, and this is the case that
   * used to be silent. When only one of the two names resolves, the board is
   * no longer a choice -- one board is not a choice -- so every task on the
   * desk files to the survivor whatever team raised it, and the composer said
   * "Goes to Internal Sales" as though that were the answer. It reads as
   * everybody's tasks landing on the wrong board for no reason, which is
   * exactly how it was reported. Naming the board that did not resolve turns
   * it back into a setting somebody can fix.
   */
  const partlyMatched = (boards.data?.boards.length ?? 0) > 0
    ? (boards.data?.unmatched ?? [])
    : [];

  /*
   * Who this person may hand it to. A manager gets the whole Lane directory; a
   * rep gets the sales teams plus the account manager and themselves. Decided
   * server-side and only rendered here -- offering names the server would
   * reject makes the restriction feel like a bug.
   */
  const eligible = boards.data?.assignees ?? [];
  const self = boards.data?.selfAssigneeId ?? "";

  /* The account manager, whichever team they sit on. A task about an account
     belongs to whoever manages it. */
  const managed = boards.data?.managerAssigneeId ?? "";

  /*
   * A follow-up lands on **you** unless you say otherwise, which is a different
   * default from the account-manager fallback the Task mode below uses. The
   * difference is the room: a task coming out of a meeting is nearly always
   * something the person who was in it has just promised to do, and handing it
   * to the account manager would quietly reassign somebody else's promise. It
   * is the same rule the meetings diary follows, deliberately -- the two ways
   * into this should not disagree about whose job it is.
   */
  const followUpAssignee = followUpBy ?? self;

  /**
   * What will be raised when this meeting is saved.
   *
   * Derived rather than held, so the ticked chips and the typed line are the
   * only state and cannot drift out of step with a separate list. `key`
   * survives the round trip so whatever succeeded can be taken off the queue
   * without matching on the title, which two quick tasks could share.
   */
  const followUps = ((): { key: string; title: string; dueAt?: string }[] => {
    const quick = (quickTasks.data ?? [])
      .filter((task) => quickPicked.includes(task.id))
      .map((task) => ({
        key: `quick:${task.id}`,
        title: task.label,
        /* The manager's own "in N days" on the quick task, not the date box --
           that one belongs to the line somebody typed. */
        ...(typeof task.dueInDays === "number"
          ? { dueAt: dueAtInDays(task.dueInDays) }
          : {}),
      }));

    const typed = followUpTitle.trim();
    if (!typed) return quick;

    return [
      ...quick,
      {
        key: "typed",
        title: typed,
        ...(followUpDue ? { dueAt: dueAtOn(followUpDue) } : {}),
      },
    ];
  })();

  /** The most-dealt-with person, if they can hold a task. */
  const mostContact = (() => {
    const top = contacted.data?.candidates[0];
    if (!top) return null;

    const member = eligible.find(
      (entry) => entry.name !== null && entry.name === top.name,
    );
    return member ? { id: member.id, label: top.name, count: top.total } : null;
  })();

  /*
   * Account manager, then whoever has dealt with them most, then you. Same
   * order the server falls back in, so what is shown here is what gets created.
   */
  const effectiveAssignee = assigneeId || managed || mostContact?.id || self;
  const nobodyEligible =
    boards.data !== undefined && suggested !== null && eligible.length === 0;

  /** "Rachel Poland", or "Jade Ruysseveldt (you)". */
  const nameFor = (id: string): string | null => {
    const member = eligible.find((entry) => entry.id === id);
    if (!member) return null;

    const name = member.name ?? member.email;
    return id === self ? `${name} (you)` : name;
  };

  const reset = (): void => {
    setBody("");
    setSubject("");
    setDuration("");
    setReference("");
    setNetTotal("");
    setLocation("");
    setStartsAt("");
    setAssigneeId("");
    setChangingAssignee(false);
    setQuickPicked([]);
    setFollowUpTitle("");
    setFollowUpDue("");
    setFollowUpBy(null);
    setFollowUpError(null);
    setLoggedMeeting(null);
    setFiles([]);
    setUploadError(null);
    onModeChange(null);
  };

  /**
   * Send the bytes as soon as they are chosen, rather than on save.
   *
   * A ten-megabyte photo uploading while somebody finishes typing is invisible;
   * the same upload starting when they press Save is a form that appears to
   * hang. It also means the failure -- a file too big, a dropped connection --
   * lands next to the file that caused it, while they can still remove it.
   */
  const upload = useMutation({
    mutationFn: (chosen: File[]) =>
      Promise.all(chosen.map((file) => crmApi.uploadAttachment(file))),
    onSuccess: (uploaded) => {
      setUploadError(null);
      setFiles((current) => [...current, ...uploaded]);
    },
    onError: (cause: unknown) =>
      setUploadError(
        cause instanceof ApiError
          ? cause.message
          : "That file could not be uploaded.",
      ),
  });

  /** Ten is the server's limit too, so the refusal happens before the upload. */
  const MAX_FILES = 10;

  const takeFiles = (chosen: FileList | null): void => {
    if (!chosen?.length) return;

    const room = MAX_FILES - files.length;
    if (room <= 0) {
      setUploadError(`Up to ${MAX_FILES} files on one entry.`);
      return;
    }

    upload.mutate(Array.from(chosen).slice(0, room));
  };

  /**
   * Raise the queued follow-ups, and report which would not go.
   *
   * One at a time rather than all at once: each is a call into Lane, and a desk
   * that ticked four boxes would otherwise fire four together and, when the
   * rate limit bit, have no way of telling which of them landed.
   *
   * Whatever succeeded is taken off the queue before anything is reported, and
   * this deliberately does not throw. The meeting is already written down by
   * the time it runs, so a retry that raised "Send the switch quote" a second
   * time would be a worse failure than the one being recovered from.
   */
  const raiseFollowUps = async (
    contact: number | null,
  ): Promise<{ failed: string[]; reason: string | null }> => {
    const failed: string[] = [];
    const raised: string[] = [];
    let reason: string | null = null;

    for (const item of followUps) {
      try {
        await crmApi.createTask(companyId, {
          title: item.title,
          /*
           * The sentence the meetings diary writes, word for word. A Lane task
           * carries no company of its own, so this is the only thing telling
           * whoever picks it up which visit it came out of -- and a task raised
           * here should be indistinguishable on the board from one raised from
           * the diary.
           */
          description: `Raised from the meeting with ${companyName} on ${meetingWhen(startsAt)}.`,
          ...(item.dueAt ? { dueAt: item.dueAt } : {}),
          /* Empty only while Lane has not answered yet, and then the server
             falls back rather than raising one for nobody. */
          ...(followUpAssignee ? { assigneeIds: [followUpAssignee] } : {}),
          contactId: contact,
        });
        raised.push(item.key);
      } catch (cause) {
        failed.push(item.title);
        /* The server's own sentence, with its full stop taken off -- it is
           quoted mid-sentence below, and "for CRM tasks.. Press Save" is how
           it read otherwise. */
        reason ??=
          cause instanceof ApiError
            ? cause.message.replace(/\.\s*$/, "")
            : null;
      }
    }

    if (raised.includes("typed")) setFollowUpTitle("");

    const done = new Set(
      raised
        .filter((key) => key.startsWith("quick:"))
        .map((key) => Number(key.slice("quick:".length))),
    );
    if (done.size > 0) {
      setQuickPicked((current) => current.filter((id) => !done.has(id)));
    }

    return { failed, reason };
  };

  const submit = useMutation({
    mutationFn: async () => {
      const contact = contactId ? Number(contactId) : null;

      switch (mode) {
        case "note":
          return crmApi.addNote(companyId, toHtml(body), contact, files);
        case "call":
          return crmApi.logCall(companyId, {
            direction,
            contactId: contact,
            subject: subject.trim() || undefined,
            body: body.trim() ? toHtml(body) : undefined,
            durationMinutes: duration ? Number(duration) : undefined,
            attachments: files,
          });
        case "meeting": {
          /*
           * The visit, then what came out of it -- and the id is kept, because
           * the second half can fail on its own. Logging the meeting again on
           * the retry would leave the timeline saying they were seen twice.
           */
          let meetingId = loggedMeeting;
          if (meetingId === null) {
            const logged = await crmApi.logMeeting(companyId, {
              subject: subject.trim(),
              body: body.trim() ? toHtml(body) : undefined,
              contactId: contact,
              startsAt: new Date(startsAt).toISOString(),
              location: location.trim() || undefined,
              attachments: files,
            });
            meetingId = logged.id;
            setLoggedMeeting(meetingId);
          }

          return { id: meetingId, followUps: await raiseFollowUps(contact) };
        }
        case "whatsapp":
        case "email":
          return crmApi.logMessage(companyId, {
            type: mode,
            direction,
            contactId: contact,
            subject: subject.trim() || undefined,
            body: body.trim() ? toHtml(body) : undefined,
            attachments: files,
          });
        case "external_ticket":
          return crmApi.logExternalTicket(companyId, {
            subject: subject.trim(),
            reference: reference.trim() || undefined,
            body: body.trim() ? toHtml(body) : undefined,
            contactId: contact,
            attachments: files,
          });
        case "quote":
          return crmApi.addReference(companyId, {
            type: "quote",
            reference: reference.trim(),
            outcome,
            netTotal: parseMoney(netTotal),
            body: body.trim() ? toHtml(body) : undefined,
            contactId: contact,
            attachments: files,
          });
        case "order":
          return crmApi.addReference(companyId, {
            type: "order",
            reference: reference.trim(),
            /* No outcome: an order is the answer to a quote, not another
               question. The server drops it for this type regardless. */
            netTotal: parseMoney(netTotal),
            /* Dated when it was placed, not when it was typed in. An order
               written up on Monday for the Friday before belongs on Friday --
               it is the last-order date the chase lists read. */
            occurredAt: startsAt ? new Date(startsAt).toISOString() : undefined,
            body: body.trim() ? toHtml(body) : undefined,
            contactId: contact,
            attachments: files,
          });
        case "task":
          return crmApi.createTask(companyId, {
            title: subject.trim(),
            description: body.trim() || undefined,
            /*
             * No board -- the server resolves that from the agent's team. The
             * assignee is sent explicitly because it is now required, and the
             * one shown is the one that must be raised.
             */
            contactId: contact,
            assigneeIds: [effectiveAssignee],
          });
      }
    },
    onSuccess: (result) => {
      /*
       * The timeline first and unconditionally: the entry itself was written
       * even in the case below, where only a follow-up afterwards failed.
       */
      onDone();

      const trouble = result && "followUps" in result ? result.followUps : null;
      if (trouble && trouble.failed.length > 0) {
        /* Left open, with the failures still ticked. Saying "saved" and
           closing would lose a task somebody had asked for, with nothing on
           screen to say it never existed. */
        setFollowUpError(
          /* An em dash, not the `--` this codebase writes in comments: this
             one is read by a person on screen. */
          `The meeting is saved, but ${trouble.failed.join(" and ")} could not be raised in Lane${
            trouble.reason ? ` — ${trouble.reason}` : ""
          }. Press Save to try again, or Cancel and raise it from the Tasks section.`,
        );
        return;
      }

      reset();
    },
  });

  /** Whether there is enough here to be worth writing down. */
  const ready = ((): boolean => {
    switch (mode) {
      case null:
        return false;
      case "note":
        /* A photo of a damaged delivery with nothing typed is a note. Asking
           for a caption before it can be saved is asking for "see attached". */
        return body.trim() !== "" || files.length > 0;
      /* A call or a message with nothing typed is still a record that contact
         happened, which is the fact the dormancy rules read. */
      case "call":
      case "whatsapp":
      case "email":
        return true;
      case "meeting":
        return subject.trim() !== "" && startsAt !== "";
      case "external_ticket":
        return subject.trim() !== "";
      /* The reference is the whole point of the record -- it is what a later
         sync against Team will match on -- so it is the one thing required.
         The value is not: half a record beats none. */
      case "quote":
      case "order":
        return reference.trim() !== "";
      case "task":
        /*
         * A title, and somewhere for it to go. The board is resolved by the
         * server, so what is checked here is that one exists at all -- pressing
         * Save into a desk with no sales board configured would only produce an
         * error.
         */
        return (
          subject.trim() !== "" && suggested !== null && effectiveAssignee !== ""
        );
    }
  })();

  /*
   * Nothing at all when shut. There was a dashed "add a note, log a call…"
   * prompt here, and it was a third way in beside the header's buttons and
   * each section's own -- three affordances for one action, taking a band off
   * the top of the timeline on every visit to advertise something already
   * offered twice above it.
   */
  if (!open) return null;

  return (
    <form
      ref={form}
      onSubmit={(event) => {
        event.preventDefault();
        submit.mutate();
      }}
      /* `scroll-mt`, because the page this sits on has a pinned header: the
         effect above scrolls this to the top of the scroller, and without the
         margin the top of it lands underneath that bar. */
      className="scroll-mt-4 border-b border-line px-4 py-3"
    >
      <div className="mb-2 flex flex-wrap gap-1">
        {MODES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setMode(entry.id)}
            disabled={entry.id === "task" && taskBlockedReason !== null}
            title={
              entry.id === "task" ? (taskBlockedReason ?? undefined) : undefined
            }
            className={cn(
              "rounded-full px-2.5 py-1 text-xs transition-colors",
              mode === entry.id
                ? "bg-brand-600 text-white"
                : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
              entry.id === "task" &&
                taskBlockedReason !== null &&
                "cursor-not-allowed opacity-40",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {mode === "call" || MESSAGE_MODES.has(mode!) ? (
          <>
            <Field label="Direction">
              <Picker
                value={direction}
                onChange={(value) => setDirection(value as "inbound" | "outbound")}
                options={DIRECTIONS[mode as "call" | "whatsapp" | "email"]}
                placeholder="Direction"
              />
            </Field>
            {/* Only a call is timed. A WhatsApp thread has no duration
                anybody could give an honest number for. */}
            {mode === "call" ? (
              <Field label="Minutes" hint="Optional">
                <Input value={duration} onChange={setDuration} type="number" />
              </Field>
            ) : null}
          </>
        ) : null}

        {mode === "external_ticket" ? (
          <Field
            label="Their reference"
            hint="Optional — the case number, if you have it"
          >
            <Input value={reference} onChange={setReference} />
          </Field>
        ) : null}

        {mode === "meeting" ? (
          <>
            <Field label="When" required hint="Now — change it if it was not">
              <Input
                type="datetime-local"
                value={startsAt}
                onChange={setStartsAt}
                required
              />
            </Field>
            <Field label="Where">
              <Input value={location} onChange={setLocation} />
            </Field>
          </>
        ) : null}

        {mode === "quote" ? (
          <>
            <Field label="Quote reference" required hint="As it appears in Team">
              <Input value={reference} onChange={setReference} required />
            </Field>
            <Field label="Outcome">
              <Picker
                value={outcome}
                onChange={(value) => setOutcome(value as typeof outcome)}
                options={[
                  { value: "open", label: "Still open" },
                  { value: "won", label: "Won" },
                  { value: "lost", label: "Lost" },
                ]}
                placeholder="Outcome"
              />
            </Field>
            <MoneyField value={netTotal} onChange={setNetTotal} />
          </>
        ) : null}

        {mode === "order" ? (
          <>
            <Field label="Order reference" required hint="As it appears in Team">
              <Input value={reference} onChange={setReference} required />
            </Field>
            <Field label="Placed on" hint="Now — change it if it was not">
              <Input
                type="datetime-local"
                value={startsAt}
                onChange={setStartsAt}
              />
            </Field>
            <MoneyField value={netTotal} onChange={setNetTotal} />
          </>
        ) : null}

        {mode === "task" ? (
          <Field
            label="Assigned to"
            className="sm:col-span-2"
            hint={
              partlyMatched.length > 0
                ? `Goes to ${destination}. No Lane board matches ${partlyMatched.join(" or ")}, so everything files here until a manager checks CRM_LANE_BOARDS.`
                : nobodyEligible
                ? `Goes to ${destination}, but neither you nor anybody on that team has a Lane account to own it.`
                : destination
                ? `Goes to ${destination}. Somebody on that team owns it, or you do.`
                : noBoard
                  ? `No Lane board matches ${boards.data?.unmatched.join(" or ")} — a manager needs to check CRM_LANE_BOARDS.`
                  : undefined
            }
          >
            {/*
              The board is not asked for. It follows whichever sales team the
              agent is in, which was already the preselection and was right
              every time -- so it is shown as a fact instead of a choice.

              The assignee is a choice, but never an empty one: the account
              manager is who a task about their account belongs to, and failing
              that it is yours. "Change" is there because covering for somebody
              is routine, and because being defaulted a task you meant for
              somebody else should take one click to fix.
            */}
            {/*
              The suggestion, beside the picker rather than applied silently.
              Who has answered the mail is strong evidence and not a decision --
              and it is only the default when nobody manages the account.
            */}
            {mostContact !== null &&
            mostContact.id !== effectiveAssignee &&
            (changingAssignee || effectiveAssignee === "") ? (
              <button
                type="button"
                onClick={() => {
                  setAssigneeId(mostContact.id);
                  setChangingAssignee(false);
                }}
                className="mb-1 block text-[11px] text-brand-600 hover:underline"
              >
                {mostContact.label} has dealt with them most ({mostContact.count})
              </button>
            ) : null}

            {changingAssignee || effectiveAssignee === "" ? (
              <Picker
                value={effectiveAssignee}
                onChange={setAssigneeId}
                placeholder={
                  nobodyEligible ? "Nobody on that team is in Lane" : "Choose someone"
                }
                options={eligible.map((member) => ({
                  value: member.id,
                  label: nameFor(member.id) ?? member.email,
                }))}
              />
            ) : (
              <div className="flex items-center gap-2 text-sm text-content">
                <span>{nameFor(effectiveAssignee) ?? "Assigned"}</span>
                <button
                  type="button"
                  onClick={() => setChangingAssignee(true)}
                  className="text-xs text-brand-600 hover:text-brand-700"
                >
                  Change
                </button>
              </div>
            )}
          </Field>
        ) : null}

        {/*
          Not on a note, and not on a quote or an order.

          A note *is* its body. A quote and an order have their subject built by
          the server from the type and the reference -- "Order SO-99001" -- so a
          box here was collected and then thrown away, which is worse than not
          offering it. Anything worth adding goes in the body below, where it is
          kept.
        */}
        {mode !== "note" && mode !== "quote" && mode !== "order" ? (
          <Field
            label={
              mode === "task"
                ? "Task title"
                : mode === "external_ticket"
                  ? "What it was about"
                  : "Subject"
            }
            required={
              mode === "meeting" || mode === "task" || mode === "external_ticket"
            }
            className="sm:col-span-2"
          >
            <Input value={subject} onChange={setSubject} />
          </Field>
        ) : null}

        {contacts.length > 0 ? (
          <Field label="Person" className="sm:col-span-2">
            <Picker
              value={contactId}
              onChange={setContactId}
              placeholder="Nobody in particular"
              options={contacts.map((contact) => ({
                value: String(contact.id),
                label: contact.name ?? contact.email,
              }))}
            />
          </Field>
        ) : null}
      </div>

      <textarea
        ref={bodyBox}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={3}
        placeholder={
          mode === "quote"
            ? "Why it was won or lost. This is the bit nobody can reconstruct later."
            : mode === "note"
              ? "What happened?"
              : mode === "external_ticket"
                ? "Where it was raised and what came of it (optional)"
                : "Anything worth remembering (optional)"
        }
        className="mt-2 w-full rounded-md border border-line bg-app px-2 py-1.5 text-sm text-content outline-none focus:border-brand-400"
      />

      {/*
        What has to happen next, raised with the write-up rather than after it.

        The meetings diary has had this since it was built -- a visit is logged,
        and the two jobs it created are raised from the same screen while
        somebody still remembers what they promised. Writing the same meeting up
        on the company page offered no such thing: the Task mode above is a
        separate entry somebody has to think of, come back for, and word again,
        which in practice means the promise goes unrecorded.

        The chips are the desk's standing jobs, the same list the diary and the
        Tasks section show, and ticking several is the common case -- "send the
        quote" and "chase it Friday" come out of one meeting. They are queued
        rather than raised on the click, because the meeting they hang off does
        not exist yet, and a task pointing at a visit nobody finished writing up
        would be the wrong way round.
      */}
      {mode === "meeting" ? (
        <div className="mt-3 border-t border-line pt-3">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-faint">
            Follow-up
          </p>

          {/*
            Shown even when it cannot be used, and saying why -- the same
            choice the mode row above makes, where Task stays on screen
            disabled rather than disappearing. A control that vanishes when
            the server is missing a setting is indistinguishable from one that
            was never built, so the desk reports it as missing and an admin
            never learns there is an OAuth client to add.
          */}
          {taskBlockedReason !== null ? (
            <p className="text-[11px] text-faint">{taskBlockedReason}</p>
          ) : noBoard ? (
            /* Nothing offered rather than a form that cannot work: every task
               here would land nowhere, and finding that out on Save would cost
               somebody the write-up as well. */
            <p className="text-[11px] text-faint">
              {boards.data && boards.data.unmatched.length > 0
                ? `No Lane board matches ${boards.data.unmatched.join(" or ")} — a manager needs to check CRM_LANE_BOARDS before tasks can be raised.`
                : "No sales board is configured, so a task raised here would have nowhere to go — a manager needs to set CRM_LANE_BOARDS."}
            </p>
          ) : (
            <>
              {(quickTasks.data ?? []).length > 0 ? (
                <div className="mb-2 flex flex-wrap gap-1">
                  {(quickTasks.data ?? []).map((task) => {
                    const picked = quickPicked.includes(task.id);

                    return (
                      <button
                        key={task.id}
                        type="button"
                        aria-pressed={picked}
                        title={task.description ?? undefined}
                        onClick={() =>
                          setQuickPicked((current) =>
                            picked
                              ? current.filter((id) => id !== task.id)
                              : [...current, task.id],
                          )
                        }
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs transition-colors",
                          picked
                            ? "border-brand-600 bg-brand-600 text-white"
                            : "border-line text-muted hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400",
                        )}
                      >
                        {picked ? "✓" : "+"} {task.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <input
                value={followUpTitle}
                onChange={(event) => setFollowUpTitle(event.target.value)}
                aria-label="Another follow-up"
                placeholder="Or something else — send the switch quote"
                className="mb-2 w-full rounded-md border border-line bg-app px-2 py-1.5 text-sm text-content outline-none placeholder:text-faint focus:border-brand-400"
              />

              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <Select
                  value={followUpAssignee}
                  onChange={setFollowUpBy}
                  label="Who does the follow-up"
                  options={[
                    /* You first, and preselected. The rest of the desk follows
                       in the order Lane lists them. */
                    ...(self
                      ? [{ value: self, label: "You" }]
                      : [
                          /* Only while Lane has not answered, or when you have
                             no account of your own. Empty is not "nobody" --
                             the server falls back to the account manager. */
                          { value: "", label: "Whoever it should" },
                        ]),
                    ...(boards.data?.assignees ?? [])
                      .filter((person) => person.id !== self)
                      .map((person) => ({
                        value: person.id,
                        label: person.name ?? person.email,
                      })),
                  ]}
                />

                <label className="flex items-center gap-1.5">
                  due
                  <input
                    type="date"
                    value={followUpDue}
                    onChange={(event) => setFollowUpDue(event.target.value)}
                    aria-label="Follow-up due date"
                    className="h-7 rounded-md border border-line bg-app px-1.5 text-xs text-content"
                  />
                </label>
              </div>

              {/* Said before Save rather than discovered after it: this form's
                  button writes a meeting down, and it should be plain that it
                  is about to create work in another product too. */}
              {followUps.length > 0 ? (
                <p className="mt-1.5 text-[11px] text-faint">
                  {followUps.length === 1
                    ? "One task"
                    : `${followUps.length} tasks`}{" "}
                  raised in Lane on Save
                  {destination ? `, on ${destination}` : ""}.
                </p>
              ) : null}
            </>
          )}

          {followUpError ? (
            <p className="mt-1.5 text-xs text-sla-breached">{followUpError}</p>
          ) : null}
        </div>
      ) : null}

      {/*
        Files, on every mode rather than notes alone. The composer is one
        control with a mode row, so a photo that could be attached to a note
        and not to the call it came out of would read as a bug -- and the
        server keys them to the activity, not to the kind of activity.
      */}
      {files.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((file) => (
            <li
              key={file.storageKey}
              className="flex items-center gap-1.5 rounded-md border border-line bg-app px-2 py-1 text-xs text-muted"
            >
              <span className="max-w-[16rem] truncate">{file.filename}</span>
              <span className="text-faint">{formatBytes(file.sizeBytes)}</span>
              <button
                type="button"
                onClick={() =>
                  setFiles((current) =>
                    current.filter(
                      (entry) => entry.storageKey !== file.storageKey,
                    ),
                  )
                }
                aria-label={`Remove ${file.filename}`}
                className="text-faint hover:text-sla-breached"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {uploadError ? (
        <p className="mt-1 text-xs text-sla-breached">{uploadError}</p>
      ) : null}

      {submit.isError ? (
        <p className="mt-1 text-xs text-sla-breached">
          {submit.error instanceof ApiError
            ? submit.error.message
            : "Could not save that."}
        </p>
      ) : null}

      <div className="mt-2 flex items-center justify-end gap-2">
        <input
          ref={filePicker}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            takeFiles(event.target.files);
            /* Cleared so choosing the same file twice in a row still fires. */
            event.target.value = "";
          }}
        />
        {/*
          Not on tasks. Those are raised in Lane, which stores its own
          attachments -- a file dropped here would stay in Pulse and be
          invisible to whoever picks the task up, which is worse than not
          offering it.
        */}
        {mode !== "task" ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => filePicker.current?.click()}
            disabled={upload.isPending}
            className="mr-auto"
          >
            {upload.isPending ? "Uploading…" : "Attach"}
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" onClick={reset}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!ready || submit.isPending}>
          {submit.isPending
            ? "Saving…"
            : mode === "task"
              ? "Raise in Lane"
              : /* The meeting went in on the last press and only a follow-up is
                   outstanding, so "Save" would be offering to do it again. */
                loggedMeeting !== null
                ? "Raise the follow-up"
                : "Save"}
        </Button>
      </div>
    </form>
  );
}
